import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import sanitizeText from '@/utils/sanitizeText';
import { 
  FaCog, 
  FaPlay, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaSpinner,
  FaInfoCircle
} from 'react-icons/fa';
import './RouterBulkOperations.scss';
import * as Sentry from '@sentry/react';

/**
 * RouterBulkOperations Component
 * 
 * Provides UI for deploying configuration changes to multiple routers simultaneously.
 * Supports safe mode, rollback, and progress tracking for bulk operations.
 * 
 * Features:
 * - Multi-router selection
 * - Script/command templates
 * - Safe mode protection
 * - Real-time progress tracking
 * - Bulk deployment history
 */
const RouterBulkOperations = ({ routerId = null }) => {
  const { apiFetch } = useAuth();
  const [routers, setRouters] = useState([]);
  const [selectedRouters, setSelectedRouters] = useState([]);
  const [script, setScript] = useState('');
  const [operationName, setOperationName] = useState('');
  const [useSafeMode, setUseSafeMode] = useState(true);
  const [safeModeDuration, setSafeModeDuration] = useState(180);
  const [executing, setExecuting] = useState(false);
  const [operations, setOperations] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  /**
   * Loads all enabled routers for bulk operations
   */
  const loadRouters = useCallback(async () => {
    try {
      const response = await apiFetch('/api/RouterConfig/enabled');
      if (!response.ok) {
        throw new Error('Failed to load routers');
      }
      const data = await response.json();
      setRouters(data.success ? data.data : []);

      // Pre-select current router if provided
      if (routerId) {
        setSelectedRouters([routerId]);
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading routers:', err);
      setError(err.message || 'Failed to load routers');
    }
  }, [apiFetch, routerId]);

  /**
   * Loads bulk operation history
   */
  const loadOperations = useCallback(async () => {
    try {
      const response = await apiFetch('/api/RouterConfig/bulk-operations');
      if (!response.ok) {
        throw new Error('Failed to load operations');
      }
      const data = await response.json();
      setOperations(data.success ? data.data : []);
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading operations:', err);
    }
  }, [apiFetch]);

  useEffect(() => {
    (async () => {
      await loadRouters();
      await loadOperations();
    })();
  }, [loadRouters, loadOperations]);

  /**
   * Executes bulk operation on selected routers
   */
  const handleExecute = async () => {
    if (selectedRouters.length === 0) {
      setError('Please select at least one router');
      return;
    }

    if (!script.trim()) {
      setError('Please enter a script or command to execute');
      return;
    }

    if (!operationName.trim()) {
      setError('Please enter an operation name');
      return;
    }

    setExecuting(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        operationName: operationName,
        routerIds: selectedRouters,
        script: script,
        useSafeMode: useSafeMode,
        safeModeDurationSeconds: safeModeDuration
      };

      const response = await apiFetch('/api/RouterConfig/bulk-operations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to execute bulk operation');
      }

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Bulk operation "${operationName}" completed successfully on ${selectedRouters.length} router(s)`);
        setOperationName('');
        setScript('');
        setSelectedRouters(routerId ? [routerId] : []);
        await loadOperations();
      } else {
        setError(data.error || 'Bulk operation failed');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error executing bulk operation:', err);
      setError(err.message || 'Failed to execute bulk operation');
    } finally {
      setExecuting(false);
    }
  };

  /**
   * Toggles router selection
   */
  const toggleRouterSelection = (id) => {
    setSelectedRouters(prev => 
      prev.includes(id) 
        ? prev.filter(r => r !== id)
        : [...prev, id]
    );
  };

  /**
   * Selects all routers
   */
  const selectAllRouters = () => {
    setSelectedRouters(routers.map(r => r.id));
  };

  /**
   * Deselects all routers
   */
  const deselectAllRouters = () => {
    setSelectedRouters([]);
  };

  /**
   * Formats timestamp for display
   */
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  /**
   * Gets status badge for operation
   */
  const getStatusBadge = (status) => {
    const statusConfig = {
      'Completed': { color: '#4caf50', icon: FaCheckCircle },
      'Failed': { color: '#f44336', icon: FaExclamationTriangle },
      'InProgress': { color: '#ff9800', icon: FaSpinner }
    };

    const config = statusConfig[status] || statusConfig['InProgress'];
    const Icon = config.icon;

    return (
      <span className="status-badge" style={{ color: config.color }}>
        <Icon className={status === 'InProgress' ? 'fa-spin' : ''} />
        {status}
      </span>
    );
  };

  return (
    <div className="router-bulk-operations">
      <div className="operations-header">
        <h3><FaCog /> Bulk Configuration Deployment</h3>
        <p className="description">
          Deploy configuration changes to multiple routers simultaneously with safe mode protection
        </p>
      </div>

      {error && (
        <div className="alert alert-error">
          <FaExclamationTriangle />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="close-btn">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <FaCheckCircle />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="close-btn">×</button>
        </div>
      )}

      <div className="operations-form">
        {/* Operation Name */}
        <div className="form-group">
          <label htmlFor="operationName">
            <FaInfoCircle /> Operation Name <span className="required">*</span>
          </label>
          <input
            id="operationName"
            type="text"
            value={operationName}
            onChange={(e) => setOperationName(e.target.value)}
            placeholder="e.g., Update firewall rules"
            disabled={executing}
          />
        </div>

        {/* Router Selection */}
        <div className="form-group">
          <label>
            <FaCog /> Select Routers <span className="required">*</span>
          </label>
          <div className="router-selection-actions">
            <button 
              onClick={selectAllRouters} 
              className="btn-secondary"
              disabled={executing}
            >
              Select All
            </button>
            <button 
              onClick={deselectAllRouters} 
              className="btn-secondary"
              disabled={executing}
            >
              Deselect All
            </button>
            <span className="selection-count">
              {selectedRouters.length} of {routers.length} selected
            </span>
          </div>
          <div className="router-list">
            {routers.map(router => (
              <label key={router.id} className="router-checkbox">
                <input
                  type="checkbox"
                  checked={selectedRouters.includes(router.id)}
                  onChange={() => toggleRouterSelection(router.id)}
                  disabled={executing}
                />
                <span>{sanitizeText(router.displayName || router.ipAddress)}</span>
                <span className="router-location">({sanitizeText(router.location || 'No location')})</span>
              </label>
            ))}
          </div>
        </div>

        {/* Script/Command */}
        <div className="form-group">
          <label htmlFor="script">
            <FaCog /> RouterOS Script/Commands <span className="required">*</span>
          </label>
          <textarea
            id="script"
            value={script}
            onChange={(e) => setScript(e.target.value)}
            placeholder="/ip firewall filter add chain=input action=accept protocol=tcp dst-port=8291"
            rows={8}
            disabled={executing}
          />
          <small className="help-text">
            Enter RouterOS commands or scripts. Each line will be executed sequentially.
          </small>
        </div>

        {/* Safe Mode Options */}
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={useSafeMode}
              onChange={(e) => setUseSafeMode(e.target.checked)}
              disabled={executing}
            />
            <span>Use Safe Mode (Recommended)</span>
          </label>
          {useSafeMode && (
            <div className="safe-mode-options">
              <label htmlFor="safeModeDuration">Safe Mode Duration (seconds)</label>
              <input
                id="safeModeDuration"
                type="number"
                value={safeModeDuration}
                onChange={(e) => setSafeModeDuration(parseInt(e.target.value))}
                min={60}
                max={600}
                disabled={executing}
              />
              <small className="help-text">
                Changes will be automatically rolled back if not confirmed within this duration
              </small>
            </div>
          )}
        </div>

        {/* Execute Button */}
        <div className="form-actions">
          <button 
            onClick={handleExecute} 
            className="btn-primary"
            disabled={executing || selectedRouters.length === 0 || !script.trim() || !operationName.trim()}
          >
            {executing ? (
              <>
                <FaSpinner className="fa-spin" />
                Executing...
              </>
            ) : (
              <>
                <FaPlay />
                Execute Bulk Operation
              </>
            )}
          </button>
        </div>
      </div>

      {/* Operation History */}
      <div className="operations-history">
        <h4>Bulk Operation History</h4>
        {operations.length === 0 ? (
          <p className="no-data">No bulk operations executed yet</p>
        ) : (
          <div className="operations-table">
            <table>
              <thead>
                <tr>
                  <th>Operation Name</th>
                  <th>Routers</th>
                  <th>Status</th>
                  <th>Executed At</th>
                  <th>Executed By</th>
                </tr>
              </thead>
              <tbody>
                {operations.map(op => (
                  <tr key={op.id}>
                    <td>{sanitizeText(op.operationName)}</td>
                    <td>{op.routerCount} router(s)</td>
                    <td>{getStatusBadge(op.status)}</td>
                    <td>{formatTimestamp(op.executedAt)}</td>
                    <td>{sanitizeText(op.executedBy)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouterBulkOperations;
